import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  catchError,
  combineLatest,
  debounceTime,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
//Components
import { ColorLegendComponent } from './color-legend/color-legend.component';
//Services
import { OperationsService } from 'src/app/services/operations.service';
//Models
import { Infrastructure, MultipleApiResponse, User } from '@mocca-technologies/model';

@Component({
  selector: 'app-check-near-infrastructures',
  templateUrl: './check-near-infrastructures.component.html',
  styleUrls: ['./check-near-infrastructures.component.scss'],
})
export class CheckNearInfrastructuresComponent implements OnInit, AfterViewInit, OnDestroy {
  user = JSON.parse(localStorage.getItem('user')) as User;
  @ViewChild(ColorLegendComponent) colorLegend: ColorLegendComponent;

  detailedInfras: Infrastructure[] = [];

  private _radius = 10000;
  radiusInput = 10000;

  isLoading = false;

  @Output() infrastructureSearch = new EventEmitter<Infrastructure[]>();
  @Output() radiusChange = new EventEmitter<void>();
  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() refreshCenter = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  // Inputs as streams (same concept as urban areas)
  private position$ = new Subject<{ lat: number; lon: number }>();
  private radius$ = new Subject<number>();

  constructor(
    private cdr: ChangeDetectorRef,
    private operationService: OperationsService,
    private translateService: TranslateService,
  ) {}

  get radius(): number {
    return this._radius;
  }

  // IMPORTANT: do NOT trigger API from the setter
  set radius(value: number) {
    this._radius = value;
  }

  onRadiusChange() {
    const value = Number(this.radiusInput);
    if (!Number.isFinite(value) || value <= 0) return;

    if (value !== this._radius) {
      this._radius = value;
      this.radiusChange.emit();
      this.radius$.next(this._radius); // triggers pipeline
    }
  }

  ngOnInit() {
    combineLatest([
      this.position$.pipe(
        debounceTime(250),
      ),
      this.radius$.pipe(startWith(this._radius)),
    ])
      .pipe(
        takeUntil(this.destroy$),

        switchMap(([pos, r]) => {
          this.isLoading = true;
          this.loadingChange.emit(true);

          const radius = r + r / 3;

          return this.operationService
            .getNearInfrastructuresBasicInfo(pos.lat, pos.lon, radius, this.user?.username, this?.user?.hashPassword)
            .pipe(
              map((res: HttpResponse<MultipleApiResponse>) => {
                const infrastructures = (res?.body?.responseObject ?? []) as Infrastructure[];
                return infrastructures.filter(Boolean);
              }),

              // update local state + child component + emit to parent
              tap((infras: Infrastructure[]) => {
                this.detailedInfras = infras;
                this.infrastructureSearch.emit(infras);

                if (this.colorLegend) {
                  this.colorLegend.infraList = infras;
                  this.colorLegend.getTypes();
                }
              }),

              catchError(() => {
                this.detailedInfras = [];
                this.infrastructureSearch.emit([]);

                if (this.colorLegend) {
                  this.colorLegend.infraList = [];
                  this.colorLegend.getTypes();
                }

                return of([] as Infrastructure[]);
              }),

              finalize(() => {
                this.isLoading = false;
                this.loadingChange.emit(false);
                this.cdr.detectChanges();
              }),
            );
        }),
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.colorLegend.bottomPosition = true;
    this.cdr.detectChanges();

    // optional: if you want types calculated even before the first API result
    // this.colorLegend.getTypes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Push new position into the stream (does NOT call API directly anymore)
   */
  updatePosition(latitude: number, longitude: number) {
    this.position$.next({ lat: latitude, lon: longitude });
  }

  /**
   * Optional: if parent sets radius programmatically and you want it to trigger a reload.
   * Call this.radius$.next(this._radius) after setting.
   */
}
